import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { BaseEntityService } from '../base-entity.service';
import { CreativeUnitPackageListingStore } from "./creative-unit-package-listing.store";
import { DefaultResponse, SessionQuery } from "../session";
import { Observable, map, tap } from "rxjs";
import { environment } from "../../../../environments/environment";
import { PublicCreativeUnitPackageListing } from "../../../../../../api/src/creative-unit-package-listing/creative-unit-package-listing.entity";
import { CreativeUnitPackageListingAddDto } from "../../../../../../api/src/creative-unit-package-listing/dtos/creative-unit-package-listing-add.dto";
import { CreativeUnitPackageListingUpdateDto } from "../../../../../../api/src/creative-unit-package-listing/dtos/creative-unit-package-listing-update.dto";
import { QueryParams } from "../collection/collection.model";
import { BusinessQuery } from "../business";

@Injectable({ providedIn: 'root' })
export class CreativeUnitPackageListingService extends BaseEntityService {

	constructor(
		private http: HttpClient,
		private contentSectionStore: CreativeUnitPackageListingStore,
		private readonly sessionQuery: SessionQuery,
		private readonly businessQuery: BusinessQuery
	) {super();}

	get(qp: QueryParams, updateStore: boolean = true): Observable<DefaultResponse<PublicCreativeUnitPackageListing[]>> {
		let {page = 1, limit = 10, sortBy, filters, query} = qp;

		if (query) {
			filters = {
				...(filters || {}), // ensure filters is defined
				query // merge in the query params, this will override any existing filters
			}
		}
		if (!this.sessionQuery.isAtLeastSuperAdmin()) {
			const businessIds = this.businessQuery.getUserBusinesses()?.map(b => b.id) || [];
			filters = {
				...(filters || {}),
				businessIds
			}
		}
		return this.http
			.post<DefaultResponse<any>>(`${environment.apiUrl}/creative-unit-package-listing/find`,
				filters || {},
				{
					params: {
						page,
						perPage: limit,
						sortBy: sortBy?.id || 'name',
						order: sortBy?.direction || 'DESC'
					}
				}
			)
			.pipe(
				map(response => ({
					totalResults: response.data.totalResults,
					data: response.data?.results,
					status: response.status,
					message: response.message,
					numPages: response.data.numPages,
					perPage: response.data.perPage,
					page: response.data.page
				})),
				tap(response => {
					if (updateStore) {
						this.contentSectionStore.set(response.data);
					}
				})
			);
	}

	getOne(id: string, packageId?: string): Observable<DefaultResponse<PublicCreativeUnitPackageListing>> {
		return this.http
			.get<DefaultResponse<PublicCreativeUnitPackageListing>>(
				`${environment.apiUrl}/creative-unit-package-listing/${id}` + (packageId ? `?packageId=${packageId}` : '')
			)
			.pipe(
				tap(res => {
					if (res.status === 'succeeded'){
						this.contentSectionStore.upsert(res.data.id, res.data);
					}
				})
			);
	}

	public create(contentSectionAddDto: CreativeUnitPackageListingAddDto): Observable<DefaultResponse<PublicCreativeUnitPackageListing>> {
		return this.http.post<DefaultResponse<PublicCreativeUnitPackageListing>>(`${environment.apiUrl}/creative-unit-package-listing`, contentSectionAddDto)
			.pipe(
				tap(res => {
					if (res.status === 'succeeded'){
						this.contentSectionStore.upsert(res.data.id, res.data)
					}
				})
			);
	}

	public update(contentSectionSlug: string, contentSectionUpdateDto: CreativeUnitPackageListingUpdateDto): Observable<DefaultResponse<PublicCreativeUnitPackageListing>> {
		return this.http.put<DefaultResponse<PublicCreativeUnitPackageListing>>(`${environment.apiUrl}/creative-unit-package-listing/${contentSectionSlug}`, contentSectionUpdateDto)
			.pipe(
				tap(res => {
					if (res.status === 'succeeded'){
						this.contentSectionStore.upsert(res.data.id, res.data);
					}

				})
			);
	}

	public delete(contentSectionSlug: string): Observable<DefaultResponse<PublicCreativeUnitPackageListing>> {
		return this.http.delete<DefaultResponse<PublicCreativeUnitPackageListing>>(`${environment.apiUrl}/creative-unit-package-listing/${contentSectionSlug}`)
			.pipe(
				tap(res => {
						if (res.status === 'succeeded') {
							this.contentSectionStore.remove(contentSectionSlug);
						}
					}
				));
	}

}
